import { Trans, withI18n } from '@lingui/react'
import classNames from 'classnames'
import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'
import { compose } from 'redux'

import { OFFER_TYPE_MIRAKL, OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import {
  AVAILABLE_PRODUCT_TEMPLATES,
  FREE_SHIPPING_PRESCRIPTION_TYPES,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRICE_TYPES,
  PRODUCT_TEMPLATES,
} from 'shared/consts/productTemplates'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import { isRebrandingActive } from 'shared/experiments/utils/rebranding'
import getRangeArray from 'shared/utils/getRangeArray'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import Button from 'views/components/atoms/Button'
import { onSubmitAnimationStyles } from 'views/components/atoms/Button/helpers'
import Form from 'views/components/atoms/Form'
import IconCart from 'views/components/atoms/Icon/IconCart'
import IconOnSubscription from 'views/components/atoms/Icon/IconOnSubscription'
import Notification from 'views/components/molecules/Notification'
import Select from 'views/components/molecules/Select'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import BuyBoxDeliveryCosts from 'views/components/organisms/BuyBoxWithVariants/BuyBoxDeliveryCosts'
import BuyBoxOdsButtonAndModal from 'views/components/organisms/BuyBoxWithVariants/BuyBoxOdsButtonAndModal'
import BuyBoxSendEmailForm from 'views/components/organisms/BuyBoxWithVariants/BuyBoxSendEmailForm'
import PrescriptionTypes from 'views/components/organisms/BuyBoxWithVariants/PrescriptionTypes'
import ProductStatus from 'views/components/organisms/ProductStatus'
import { RX_SELECT_MODAL_ID } from 'views/consts'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  isProductAvailable,
  isProductOutOfStock,
} from 'views/utils/productUtils'
import {
  i18nType,
  mountPointsType,
  ProductType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'
import { pickRequired } from 'views/utils/propTypeUtils'

import './AddToCart.scss'

const AddToCart = ({
  id,
  upid,
  prices,
  stockStatus,
  productTemplate,
  prescriptionType,
  indicationUrl,
  maxQuantity,
  showOdsWidgets,
  odsPartners,
  ICD10,
  i18n,
  mountPoints,
  publicRuntimeConfig: {
    publicConfig: { shippingCostThreshold, tenant },
    pageProperties: { experiments },
  },
  offerId,
  offerType,
  shouldRenderERxSelectModal,
  isMarketplaceProduct,
  disableAddToCartButton,
}) => {
  const isRx = productTemplate === PRODUCT_TEMPLATES.RX
  const renderPrescriptionTypes = offerType
    ? isRx && offerType === OFFER_TYPE_SAE
    : isRx
  const isMpOffer = offerType === OFFER_TYPE_MIRAKL
  const isAvailable = isProductAvailable(stockStatus)
  const isProductVariantOutOfStock = isProductOutOfStock(stockStatus)
  const options = getRangeArray({ min: 1, max: maxQuantity }).map(quantity => ({
    value: quantity,
    text: quantity.toString(),
  }))
  const productId = isMarketplace(experiments, tenant) ? upid : id
  const isRebrandingEnabled = isRebrandingActive(experiments)
  const isFreeDelivery = passedPrescriptionType => {
    switch (productTemplate) {
      case PRODUCT_TEMPLATES.OTX:
        return (
          FREE_SHIPPING_PRESCRIPTION_TYPES.includes(passedPrescriptionType) ||
          getPriceForType(prices, PRICE_TYPES.RETAIL).amount >=
            shippingCostThreshold
        )
      case PRODUCT_TEMPLATES.RX:
        return true
      default:
        return (
          getPriceForType(prices, PRICE_TYPES.RETAIL).amount >=
          shippingCostThreshold
        )
    }
  }
  const odsLabel = odsPartners?.includes('zava') ? (
    <Trans id="buyBox.link.label.onlineDoctorService.zava" />
  ) : (
    <Trans id="buyBox.link.label.onlineDoctorService" />
  )
  const odsLink = mountPoints.getOnlineDoctorService(
    odsPartners?.includes('zava') ? id : null
  )
  const quantityClassName = classNames('l-flex o-AddToCart--form', {
    'l-flex--wrap': isRx,
  })
  const quantitySelectClassName = classNames({
    'u-margin-small--right o-BuyBox__grow--one-quarter@small-tiny o-AddToCart__select-wrapper':
      !isRx,
    'l-col--6-of-12@medium l-col--4-of-12@small-tiny': isRx,
  })
  const submitClassName = classNames(
    'l-flex__primary o-BuyBox__buttonContainer',
    {
      'u-padding-small--top l-col--12-of-12': isRx,
      'o-BuyBox__grow--three-quarter@small-tiny': !isRx,
      'disable-cursor': !isAvailable || disableAddToCartButton,
    }
  )
  const buyBoxSubscriptionLinkClassName = classNames(
    'o-BuyBox__subscriptionLink',
    {
      'u-padding-small--top': !disableAddToCartButton,
    }
  )

  // TODO simplify, extract component
  const formComponent =
    isProductVariantOutOfStock && !isMarketplaceProduct ? (
      <BuyBoxSendEmailForm
        id={id}
        stockStatus={stockStatus}
        useLinkForDeliveryCosts
      />
    ) : (
      <Form
        name={`buy-${id}`}
        action={mountPoints.getAddToCart()}
        data-clientside-hook="chargeUrlSearchParams add2CartForm"
      >
        <input name="cartAction" type="hidden" value="add" />
        <input name="product" type="hidden" value={productId} />
        <input name="eventType" type="hidden" value="conversion" />
        <input name="eventName" type="hidden" value="addToCart_PDP" />
        <input name="offer" type="hidden" value={offerId} />
        {!isRx && (
          <input
            name="prescriptionType"
            type="hidden"
            value={prescriptionType}
          />
        )}
        <div className="l-flex u-margin--bottom@medium u-margin-large--bottom@small-tiny o-AddToCart--status">
          <div className="l-flex__primary">
            <ProductStatus stockStatus={stockStatus} />
          </div>
          {isAvailable && !isMpOffer && (
            <BuyBoxDeliveryCosts
              isFreeDelivery={isFreeDelivery}
              prescriptionType={prescriptionType}
              useLink
            />
          )}
        </div>
        <div className={quantityClassName}>
          {renderPrescriptionTypes && (
            <div className="l-flex__primary u-margin-small--right">
              <PrescriptionTypes
                productTemplate={productTemplate}
                selectedPrescriptionType={prescriptionType}
              />
            </div>
          )}
          <div className={quantitySelectClassName}>
            <Select
              id="product_quantity_BuyBox"
              name="quantity"
              label={i18n._('general.label.quantity')}
              floatingLabel
              disabled={!isAvailable}
              options={options}
            />
          </div>
          <div className={submitClassName}>
            <Button
              name="add-to-cart-button"
              id="AddToCartButton"
              disabled={!isAvailable || disableAddToCartButton}
              type={shouldRenderERxSelectModal ? undefined : 'submit'}
              data-loading-label={i18n._('button.loading.label')}
              href={
                shouldRenderERxSelectModal
                  ? `#${RX_SELECT_MODAL_ID}`
                  : undefined
              }
              data-submit-styles={onSubmitAnimationStyles(isRebrandingEnabled)}
            >
              <TextWithIcon
                iconClassName="u-margin-x-small--right"
                icon={<IconCart />}
              >
                <span className="a-h3 u-no-margin u-font-weight--normal o-BuyBoxWithVariants_buy-button__text">
                  <Trans id="general.button.label.addToCart" />
                </span>
              </TextWithIcon>
            </Button>
          </div>
        </div>
        {disableAddToCartButton && (
          <Notification
            icon={false}
            className="u-margin-large--bottom u-margin-large--top"
            label={i18n._(
              'pdp.messages.info.privatePrescriptionNotCombinableWithERX'
            )}
          />
        )}
      </Form>
    )

  return (
    <>
      {formComponent}
      <div
        className={buyBoxSubscriptionLinkClassName}
        data-clientside-hook="buyboxSubscriptionLink"
        aria-hidden
      >
        <Button
          style="buttonGhost"
          id="create_subscription"
          name="buybox-large_subse"
          data-a11y-dialog-show="loginModal"
          isClientsideControlled
          data-clientside-hook="buyboxSubscriptionLink__trigger"
        >
          <span className="a-h3 u-font-weight--normal u-no-margin">
            <Trans id="subscriptionClient.link.label" />
          </span>
          {isRebrandingEnabled && <IconOnSubscription />}
        </Button>
      </div>
      {showOdsWidgets && (
        <div className="u-padding-small--top">
          <BuyBoxOdsButtonAndModal
            i18n={i18n}
            videoConsultation={isRx && !odsPartners?.includes('zava')}
            odsLabel={odsLabel}
            odsLink={odsLink}
            indicationUrl={indicationUrl}
            ICD10={ICD10}
            hideIcon
          />
        </div>
      )}
    </>
  )
}

AddToCart.propTypes = {
  ...pickRequired(ProductType, ['upid', 'id', 'stockStatus', 'prices']),
  mountPoints: mountPointsType.isRequired,
  i18n: i18nType.isRequired,
  maxQuantity: number.isRequired,
  prescriptionType: string,
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES),
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  showOdsWidgets: bool,
  odsPartners: arrayOf(string),
  ICD10: arrayOf(shape({})),
  // TODO Add a “.isRequired” once we migrate fully to marketplace
  offerId: string,
  offerType: string,
  shouldRenderERxSelectModal: bool,
  isMarketplaceProduct: bool,
  disableAddToCartButton: bool,
}

AddToCart.defaultProps = {
  productTemplate: PRODUCT_TEMPLATES.OTC,
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  showOdsWidgets: false,
  odsPartners: [],
  ICD10: [],
  offerId: null,
  offerType: null,
  shouldRenderERxSelectModal: false,
  isMarketplaceProduct: false,
  disableAddToCartButton: false,
}

export default compose(
  withI18n(),
  withMountPoints,
  withShopAndRuntimeConfig
)(AddToCart)
